/** Greedy word wrap using a measuring function; a single over-long word is broken by character. */
export function wrapWords(text: string, measure: (s: string) => number, maxWidth: number): string[] {
  const out: string[] = [];
  for (const para of text.split(/\r?\n/)) {
    let line = "";
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const trial = line ? `${line} ${word}` : word;
      if (measure(trial) <= maxWidth) { line = trial; continue; }
      if (line) out.push(line);
      if (measure(word) <= maxWidth) { line = word; continue; }
      let chunk = "";
      for (const ch of Array.from(word)) {
        if (measure(chunk + ch) > maxWidth && chunk) { out.push(chunk); chunk = ch; } else chunk += ch;
      }
      line = chunk;
    }
    out.push(line);
  }
  return out.filter((l, i, a) => l !== "" || (i > 0 && i < a.length - 1));
}

/**
 * Finds the biggest font size (down to minSize) at which `text` fits in maxLines lines of maxWidth.
 * `measureAt(size)` returns a measuring function for that size. If even minSize doesn't fit, minSize is used
 * and the lines are returned as they wrap (the caller can let them run a little taller).
 */
export function fitText(text: string, measureAt: (size: number) => (s: string) => number, maxWidth: number, startSize: number, minSize: number, maxLines: number) {
  let size = startSize;
  for (; size >= minSize; size -= 2) {
    const lines = wrapWords(text, measureAt(size), maxWidth);
    if (lines.length <= maxLines) return { size, lines };
  }
  return { size: minSize, lines: wrapWords(text, measureAt(minSize), maxWidth) };
}

/**
 * Height (px) kept free at the top and bottom edge for the trial badge, which is stamped at download time in the
 * top-right and bottom-right corners. Mirrors the badge size rule in lib/trialMark.ts (markImage): H = max(22, 5.5% of the short side),
 * margin = 0.6 H. Captions are kept inside the band between the two reserved strips so the badge never sits on caption text.
 */
export function trialBadgeReserve(width: number, height: number): number {
  const H = Math.max(22, Math.round(Math.min(width, height) * 0.055));
  const margin = Math.round(H * 0.6);
  return margin + H + Math.round(margin * 0.5);
}

export interface MemeLine { text: string; x: number; y: number; size: number; strokeWidth: number }

/** Pure layout of the top and bottom captions. Returns every line to draw with its position; empty captions give no lines. */
export function layoutMeme(
  topText: string, bottomText: string, width: number, height: number,
  measureAt: (size: number) => (s: string) => number, reserve = 0,
): MemeLine[] {
  const maxWidth = width * 0.94;
  const start = Math.max(width / 9, 28);
  const min = Math.max(width / 40, 14);
  const pad = Math.max(width * 0.02, 8) + reserve;
  const out: MemeLine[] = [];
  const block = (text: string, fromTop: boolean) => {
    const t = text.toUpperCase();
    if (!t.trim()) return;
    const { size, lines } = fitText(t, measureAt, maxWidth, start, min, 3);
    const lineH = size * 1.08;
    lines.forEach((ln, i) => {
      const y = fromTop ? pad + size * 0.9 + i * lineH : height - pad - (lines.length - 1 - i) * lineH - size * 0.12;
      out.push({ text: ln, x: width / 2, y, size, strokeWidth: Math.max(size / 8, 2) });
    });
  };
  block(topText, true);
  block(bottomText, false);
  return out;
}
